/**
 * Finding someone by descent — "Hassan ben Idriss ben Mehdi".
 *
 * Most of the world does not identify a person by a surname. In a family that
 * brings a grandfather's name back every other generation, "Hassan" returns nine
 * people and identifies nobody, while *Hassan ben Idriss ben Mehdi* identifies
 * exactly one — and that is the form the family says out loud.
 *
 * Two decisions worth naming:
 *
 *  - **The answer says which parent each link ran through**, rather than the
 *    question having to assert it. `ben` and its cousins do not distinguish, and
 *    a searcher often does not know; two matches that differ only in whether the
 *    link was a mother or a father are then told apart by the result.
 *  - **`f:` and `m:` are there for when they do know** — a maternal line
 *    recorded because the father is not, or two parents whose names both fit.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LineageMatch } from '@famtree/shared';
import { personsApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import { lifespan } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

export function LineageSearch(): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState('');

  const result = useQuery({
    queryKey: ['lineage', submitted],
    queryFn: () => personsApi.lineage(submitted),
    enabled: submitted.trim().length > 0,
  });

  return (
    <section className={ui.card}>
      <h2>{t('lineage.title')}</h2>
      <p className={ui.muted}>{t('lineage.help')}</p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(draft);
        }}
      >
        <label className={ui.field}>
          <span className={ui.label}>{t('lineage.label')}</span>
          <input
            className={ui.input}
            value={draft}
            placeholder={t('lineage.placeholder')}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <button type="submit" className={ui.button} disabled={draft.trim().length === 0}>
          {t('lineage.submit')}
        </button>
      </form>

      <p className={ui.muted}>{t('lineage.sideHint')}</p>

      {result.isPending && submitted && <p className={ui.muted}>{t('app.loading')}</p>}
      {result.isError && <p className={ui.problem}>{problemMessage(result.error, t)}</p>}

      {result.isSuccess && (
        <>
          {/* What the parser understood, so a mistyped particle is visible
              rather than silently swallowing half the chain. */}
          <p className={ui.muted}>
            {t('lineage.readAs', {
              chain: result.data.segments
                .map((segment) =>
                  segment.side === 'any'
                    ? segment.name
                    : `${segment.name} (${t(`lineage.sides.${segment.side}`)})`,
                )
                .join(' ← '),
            })}
          </p>

          {result.data.matches.length === 0 ? (
            <p>{t('lineage.noMatch')}</p>
          ) : (
            <>
              <p className={ui.muted}>
                {t('lineage.matchCount', { count: result.data.matches.length })}
              </p>
              <ul className={styles.list}>
                {result.data.matches.map((match) => (
                  <li key={match.chain.map((link) => link.person.id).join('-')}>
                    <Match match={match} />
                  </li>
                ))}
              </ul>
            </>
          )}
          {result.data.truncated && <p className={ui.muted}>{t('lineage.truncated')}</p>}
        </>
      )}
    </section>
  );
}

function Match({ match }: { match: LineageMatch }): JSX.Element {
  const { t } = useTranslation();
  const [head, ...ancestors] = match.chain;
  if (!head) return <span />;

  return (
    <div className={styles.row}>
      <Link className={styles.rowName} to={`/people/${head.person.id}`}>
        {head.person.display_label}
      </Link>
      {lifespan(head.person) && <span className="tabular"> {lifespan(head.person)}</span>}
      {head.person.status === 'pending' && (
        <span className={styles.badgePending}>{t('enums.assertion_status.pending')}</span>
      )}
      <span className={ui.muted}>
        {' '}
        {ancestors.map((link, index) => (
          <span key={link.person.id}>
            {index > 0 && ' '}
            {/* The role is the point: "son of his mother Zohra" is a different
                person from "son of his father Idriss". */}
            {t(`lineage.via.${link.role ?? 'parent'}`)}{' '}
            <Link to={`/people/${link.person.id}`}>{link.person.display_label}</Link>
          </span>
        ))}
      </span>
      {!match.exact && <span className={ui.muted}> · {t('lineage.partial')}</span>}
    </div>
  );
}
